import {toast} from '@wandb/components';
import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import clsx from 'clsx';
import copyToClipboard from 'copy-to-clipboard';
import {isUndefined} from 'lodash';
import React, {memo, useState} from 'react';
import {Label} from 'semantic-ui-react';

import {
  getRunColorMethod,
  trackRunColor,
} from '../../services/analytics/runColorEvents';
import {observeRunVisibilityCriticalWorkflow} from '../../util/datadog/criticalWorkflows/perfWindow/runVisibility';
import {getSweepDisplayName} from '../../util/getSweepDisplayName';
import {isMobile} from '../../util/panelbankFlow';
import {useStatsigGateHideStopRunsUI} from '../../util/rampFeatureFlags/rampFeatureFlags';
import type {WBTableRowWithRunSetInfo} from '../../util/tablecols';
import {versionGte} from '../../util/version';
import ActiveRunIndicator from '../ActiveRunIndicator';
import DeleteRunModal from '../DeleteRunModal';
import StopRunModal from '../StopRunModal';
import {useRunHighlighter} from './hooks/useRunHighlighter';
import {
  type RunSelectorRowHeaderProps,
  useRunSelectorRowHeaderProps,
} from './hooks/useRunSelectorRowHeaderProps';
import LaunchJobIcon from './LaunchJobIcon';
import {MoveRunDrawer} from './MoveRunDrawer/MoveRunDrawer';
import {RowColorPicker} from './RowHeader/RowColorPicker';
import {RowExpandChildren} from './RowHeader/RowExpandChildren';
import {RowPinning} from './RowHeader/RowPinning';
import {RowSelection} from './RowHeader/RowSelection';
import {RowVisibility} from './RowHeader/RowVisibility';
import {RowHeaderLabel} from './RowHeaderLabel';
import {RowHeaderMenu} from './RowHeaderMenu/RowHeaderMenu';
import {useRunSelectorAnalyticsLocation} from './RunSelectorAnalyticsLocationContext';
import {useRunSelectorFeatureDisabled} from './RunSelectorFeaturesContext';
import {isCrossProjectRun} from './util';

function isRunStoppable(row: WBTableRowWithRunSetInfo): boolean {
  // TODO(adrnswanberg): Centralize this with similar logic in RunPageActions.tsx,
  // probably around when we start using the same `Run` type everywhere.
  const cliVersion = row._wandb['value.cli_version'] ?? row._wandb.cli_version;
  if (cliVersion == null || typeof cliVersion !== 'string') {
    return false;
  }

  const isJupyterRun =
    row._wandb['value.is_jupyter_run'] ?? row._wandb.is_jupyter_run ?? false;

  return versionGte(cliVersion, '0.8.6') && !isJupyterRun;
}

function showColorPicker(isGroup: boolean, recursionDepth: number) {
  // Show the color picker for all grouped rows and top-level ungrouped runs.
  return isGroup || recursionDepth === 0;
}

type AllRunSelectorRowHeaderProps = RunSelectorRowHeaderProps &
  ReturnType<typeof useRunSelectorRowHeaderProps>;

const RunSelectorRowHeaderInnerComp: React.FC<AllRunSelectorRowHeaderProps> = ({
  className,
  clearCustomRunColor,
  closeDeleteModal,
  closeStopModal,
  customRunColor,
  affectedRunCount,
  customRunColorPreviewColors,
  customRunColorSource,
  customRunName,
  customRunNamesRef,
  deleteModalOpen,
  disableRunLink,
  entityName,
  groupedBy,
  hasCustomRunColorDescendantColors,
  hasParentInheritedColor,
  isGroup,
  isGroupExpanded,
  isHoveringRow,
  isGroupingEnabled,
  isCustomRunColorClearable,
  isParentGroup,
  isLaunchActive,
  isLaunchPublicQueueActive,
  isRunGroup,
  isSingleMode,
  isTableCollapsed,
  loadingChildren,
  onGroupButtonClick,
  onSelectRunName,
  onTempCheckboxClick,
  openDeleteModal,
  openStopModal,
  projectName,
  recursionDepth,
  row,
  runIsInBounds,
  setCustomRunColor,
  setDeleteModalOpen,
  setLaunchDrawerRow,
  showArtifactCounts,
  showLogCounts,
  stopModalOpen,
  stopRun,
  tempCheckboxState,
  tempSelectToggle,
  toggleSelection,
  truncationType,
  uniqueId,
  visCheckboxState,
  groupSelectionsRef,
  selectedCount,
}) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const [moveRunDrawerOpen, setMoveRunDrawerOpen] = useState(false);
  const isDisabled = useRunSelectorFeatureDisabled();
  const {handleMouseIn, handleMouseOut} = useRunHighlighter(uniqueId);

  // shared by every color action so open vs set vs reset can be compared on one event
  const runColorEventProps = {
    affectedRunCount,
    customRunColorSource,
    hasCustomRunColorDescendantColors,
    hasParentInheritedColor,
    isGroupingEnabled,
    isParentGroup,
    location: analyticsLocation,
    rowDepth: recursionDepth,
  };

  function onCopyRunName() {
    const name = customRunName || row.displayName;
    copyToClipboard(name);
    toast.success('Copied run name to clipboard');
  }

  function onCopyRunPath() {
    // For cross-project runs, the row carries its own project info
    // that should be preferred over the workspace's entity/project.
    const runEntityName = row.project?.entityName ?? entityName;
    const runProjectName = row.project?.name ?? projectName;
    const fullPath = `${runEntityName}/${runProjectName}/${row.name}`;
    copyToClipboard(fullPath);
    toast.success('Copied run path to clipboard');
  }

  const isHideStopRunsEnabled = useStatsigGateHideStopRunsUI();
  const canStopRun =
    !isHideStopRunsEnabled &&
    row.state === 'running' &&
    (row.sweep != null || isRunStoppable(row));

  const groupCounts = row.groupCounts ?? [];

  let runCount;
  let groupCount;
  if (groupCounts.length >= 2) {
    groupCount = groupCounts[groupCounts.length - 2];
    runCount = groupCounts[groupCounts.length - 1];
  } else if (groupCounts.length === 1) {
    [runCount] = groupCounts;
  }

  const isHeaderDropdownEmpty =
    isDisabled('edit-run-name') &&
    isDisabled('stop-run') &&
    isDisabled('delete') &&
    isDisabled('baselining') &&
    isDisabled('pinning') &&
    isDisabled('move');

  const cls = clsx(
    'wb-tree-cell--header-row',
    'wb-tree-cell--name',
    className,
    {
      'wb-tree-cell--loading-children': loadingChildren,
    }
  );

  // Indirection here to trick typescript:
  const twProps = {
    className: cls,
    style: {paddingLeft: recursionDepth * 26},
    onMouseEnter: handleMouseIn,
    onMouseLeave: handleMouseOut,
  };

  return (
    <>
      <MoveRunDrawer
        entityName={entityName}
        onClose={() => setMoveRunDrawerOpen(false)}
        onMovedRuns={() => setMoveRunDrawerOpen(false)}
        open={moveRunDrawerOpen}
        projectName={projectName}
        runs={[row]}
        groupSelectionsRef={groupSelectionsRef}
        selectedCount={selectedCount}
      />
      <DeleteRunModal
        entityName={entityName}
        isRunActive={row.state === 'running'}
        isOpen={deleteModalOpen}
        setIsModalOpen={setDeleteModalOpen}
        closeModal={closeDeleteModal}
        projectName={projectName}
        runDisplayName={row.displayName}
        runId={row.id}
        runName={row.name}
      />
      <StopRunModal
        shouldShowModal={stopModalOpen}
        onModalCanceled={closeStopModal}
        onStopRun={stopRun}
      />
      <Tailwind {...twProps}>
        <RowSelection
          checkboxState={tempCheckboxState}
          isCrossProjectRun={isCrossProjectRun(row.project, {
            entityName,
            name: projectName,
          })}
          isGroup={Boolean(isGroup)}
          isTableCollapsed={Boolean(isTableCollapsed)}
          onPress={() => {
            tempSelectToggle();
            onTempCheckboxClick();
            window.analytics?.track('Run selected', {
              location: analyticsLocation,
            });
          }}
        />
        <RowVisibility
          isHiddenByParallelCoordinatesOrScatterPlot={runIsInBounds === false}
          isSingleMode={Boolean(isSingleMode)}
          onChangeVisibility={event => {
            observeRunVisibilityCriticalWorkflow({
              event,
              isGroup: Boolean(isGroup),
              visibilityState: visCheckboxState,
            });
            toggleSelection(row, recursionDepth + 1);
            window.analytics?.track('Run visibility toggled', {
              location: analyticsLocation,
            });
          }}
          visibilityState={visCheckboxState}
        />
        <RowColorPicker
          customRunColor={customRunColor}
          customRunColorPreviewColors={customRunColorPreviewColors}
          customRunColorSource={customRunColorSource}
          hasCustomRunColorDescendantColors={hasCustomRunColorDescendantColors}
          hasParentInheritedColor={hasParentInheritedColor}
          isColorPickerVisible={showColorPicker(
            Boolean(isGroup),
            recursionDepth
          )}
          isCustomRunColorClearable={isCustomRunColorClearable}
          isCrossProjectRun={isCrossProjectRun(row.project, {
            entityName,
            name: projectName,
          })}
          isParentGroup={isParentGroup}
          onClearCustomRunColor={() => {
            clearCustomRunColor();
            trackRunColor({...runColorEventProps, action: 'Reset color'});
          }}
          onOpenColorPicker={() => {
            trackRunColor({
              ...runColorEventProps,
              action: 'Open color picker',
            });
          }}
          onGradientColorInteractionEnd={() => {
            trackRunColor({
              ...runColorEventProps,
              action: 'Set color',
              method: 'gradient',
            });
          }}
          onSetCustomRunColor={(color, source, shouldTrack = true) => {
            setCustomRunColor(color);
            if (shouldTrack) {
              trackRunColor({
                ...runColorEventProps,
                action: 'Set color',
                method: getRunColorMethod(source),
              });
            }
          }}
        />
        <RowExpandChildren
          isExpanded={isGroupExpanded}
          isGroup={Boolean(isGroup)}
          isLoading={Boolean(loadingChildren)}
          onToggleExpand={onGroupButtonClick}
        />
        <RowHeaderLabel
          customSweepName={
            row?.sweep && row.sweep.name
              ? getSweepDisplayName({
                  ...row.sweep,
                  name: row.sweep.name,
                })
              : null
          }
          customRunName={customRunName}
          disableRunLink={Boolean(disableRunLink)}
          entityName={entityName}
          groupedBy={groupedBy}
          isGroup={Boolean(isGroup)}
          isHoveringRow={isHoveringRow}
          isRunGroup={isRunGroup}
          isSingleMode={Boolean(isSingleMode)}
          onSelectRunName={name => {
            if (onSelectRunName) {
              onSelectRunName(name);
            }
          }}
          projectName={projectName}
          row={row}
          truncationType={truncationType}
          uniqueId={uniqueId}
        />
        {(isLaunchActive || isLaunchPublicQueueActive) &&
          row.state !== 'running' &&
          !isMobile() && (
            <LaunchJobIcon
              projectName={projectName}
              entityName={entityName}
              row={row}
              setLaunchDrawerRow={setLaunchDrawerRow}
              isLaunchActive={isLaunchActive}
              isLaunchPublicQueueActive={isLaunchPublicQueueActive}
            />
          )}
        {isGroup && (
          <>
            {!isUndefined(groupCount) && (
              <Label className="count-label">{groupCount}</Label>
            )}
            {!isUndefined(runCount) && (
              <Label className="count-label">{runCount}</Label>
            )}
          </>
        )}
        {!isGroup && showArtifactCounts && (
          <>
            {row.inputArtifactsCount !== 0 && (
              <Label className="count-label">
                In {row.inputArtifactsCount}
              </Label>
            )}
            {row.outputArtifactsCount !== 0 && (
              <Label className="count-label">
                Out {row.outputArtifactsCount}
              </Label>
            )}
          </>
        )}

        {!isGroup && showLogCounts && row.logLineCount != null && (
          <Label className="count-label">{row.logLineCount}</Label>
        )}

        {/* groups: any distinct state running; single runs: the run itself */}
        {(isGroup
          ? (row.multiValueMetadataColumns?.state?.includes('running') ?? false)
          : row.state === 'running') && <ActiveRunIndicator />}

        <RowHeaderMenu
          canStopRun={canStopRun}
          customRunNamesRef={customRunNamesRef}
          isHeaderDropdownEmpty={Boolean(isHeaderDropdownEmpty)}
          isGroup={Boolean(isGroup)}
          onCopyRunName={onCopyRunName}
          onCopyRunPath={onCopyRunPath}
          onDeleteRun={openDeleteModal}
          onMoveRun={() => setMoveRunDrawerOpen(true)}
          onStopRun={openStopModal}
          row={row}
          visible={isHoveringRow}
        />
        <RowPinning
          isSingleMode={Boolean(isSingleMode)}
          isVisible={isHoveringRow}
          row={row}
        />
      </Tailwind>
    </>
  );
};

const RunSelectorRowHeaderInner = memo(
  RunSelectorRowHeaderInnerComp,
  (prevProps, nextProps) => {
    if (prevProps.row.state !== nextProps.row.state) {
      return false;
    }
    if (prevProps.row.displayName !== nextProps.row.displayName) {
      return false;
    }
    if (prevProps.row.groupCounts !== nextProps.row.groupCounts) {
      return false;
    }
    const propsAreEqual = makePropsAreEqual({
      name: 'RunSelectorRowHeader',
      deep: ['groupKey', 'tempCheckboxState', 'customRunColors'],
      // Can ignore, the row remounts when run id changes. This will screw up polling though
      // We should pass the value in rather than row probably, or maybe just check if our value
      // changes here in shouldUpdate
      ignore: ['row', 'cellHoverProps'],
      ignoreFunctions: true,
      debug: false,
      verbose: true,
    });
    return propsAreEqual(prevProps, nextProps);
  }
);

export const RunSelectorRowHeader = (props: RunSelectorRowHeaderProps) => {
  const reduxProps = useRunSelectorRowHeaderProps(props);
  return <RunSelectorRowHeaderInner {...props} {...reduxProps} />;
};
